import React, { useState, useEffect } from 'react';
import {
  Container, TextField, Button, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Typography, Chip, Tooltip, useMediaQuery, useTheme,
  Stack, Paper
} from '@mui/material';
import { Close as CloseIcon, Visibility, CalendarToday, Place, Person, OpenInNew, History } from '@mui/icons-material';
import TituloPagina from './utils/TituloPagina.jsx';
import { MenuInicial } from "./MenuInicial";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, USUARIOACCIONLISTAR } from '../services/apiConfig';
import { DataGrid } from '@mui/x-data-grid';
import Cargando from './utils/Cargando.jsx';
import ExportToolbar from './utils/ExportToolbar.jsx';
import axios from 'axios';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const codeBlock = (text, maxH = 200) => {
  let formatted = text == null ? '' : String(text);
  try { formatted = JSON.stringify(JSON.parse(formatted), null, 2); } catch {}
  return (
    <Box sx={{
      bgcolor: '#0d1117', color: '#e6edf3', p: 1.5, borderRadius: 1.5, mt: 0.5,
      fontFamily: 'JetBrains Mono, Consolas, monospace', fontSize: 12,
      whiteSpace: 'pre-wrap', wordBreak: 'break-all',
      maxHeight: maxH, overflow: 'auto',
      border: '1px solid #30363d',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)'
    }}>{formatted || '—'}</Box>
  );
};

function AccionDetailDialog({ open, row, onClose }) {
  if (!row) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid', borderColor: 'divider', pb: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 10, height: 10, borderRadius: '50%', bgcolor: 'info.main'
          }} />
          <span style={{ fontWeight: 600 }}>Acción #{row.id}</span>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* Ruta navegada */}
          <Box sx={{
            bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5,
            border: '1px solid', borderColor: 'divider'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <OpenInNew sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Navegó</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, Consolas, monospace', fontSize: 13, wordBreak: 'break-all' }}>
              {row.navego || '—'}
            </Typography>
          </Box>

          {/* Metadata chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip icon={<CalendarToday sx={{ fontSize: 14 }} />} label={row.fecha} size="small" variant="outlined" />
            <Chip icon={<Place sx={{ fontSize: 14 }} />} label={`IP: ${row.ip || '—'}`} size="small" variant="outlined" />
            <Chip icon={<Person sx={{ fontSize: 14 }} />} label={row.username || row.usuarioid || '—'} size="small" variant="outlined" />
          </Box>

          {/* Raw row */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Detalle completo</Typography>
            </Box>
            {codeBlock(JSON.stringify(row, null, 2), 300)}
          </Box>

        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
        <Button onClick={onClose} variant="contained" disableElevation>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function UsuarioAccionesList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const userData = JSON.parse(sessionStorage.getItem("userData"));
  const sucursalid = userData?.sucursalid;
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedRow, setSelectedRow] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    const filteredData = users.filter((user) =>
      (user.navego || '').toLowerCase().includes(term.toLowerCase())
    );
    setFilteredUsers(filteredData);
  };

  useEffect(() => {
    handleUpdateData();
  }, []);

  const handleUpdateData = () => {
    setLoading(true);

    const date = new Date(startDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const fechainiciod = `${year}-${month}-${day}`;

    const datef = new Date(endDate);
    const yearf = datef.getFullYear();
    const monthf = String(datef.getMonth() + 1).padStart(2, '0');
    const dayf = String(datef.getDate()).padStart(2, '0');
    const fechainiciof = `${yearf}-${monthf}-${dayf}`;

    axios.get(`${API_BASE_URL}${USUARIOACCIONLISTAR}?s=${sucursalid}&startDate=${fechainiciod}&endDate=${fechainiciof}`, { headers: authHeader() })
      .then((response) => {
        const payload = response.data?.data ?? response.data;
        const userData = Array.isArray(payload) ? payload : [];
        setUsers(userData);
        const filteredData = userData.filter(user => (user.navego || '').toLowerCase().includes(searchTerm.toLowerCase()));
        setFilteredUsers(filteredData);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error al cargar las acciones de usuario', error);
        setLoading(false);
      });
  };

  const handleVerMas = (row) => {
    setSelectedRow(row);
    setDialogOpen(true);
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'fecha', headerName: 'Fecha', width: 155 },
    { field: 'username', headerName: 'Usuario', width: 180 },
    {
      field: 'navego', headerName: 'Navegó', width: 350,
      renderCell: (params) => (
        <span style={{ fontSize: 12, wordBreak: 'break-word' }}>{params.value}</span>
      )
    },
    { field: 'ip', headerName: 'IP', width: 140 },
    {
      field: 'accion', headerName: '', width: 70, sortable: false,
      renderCell: (params) => (
        <Tooltip title="Ver detalle">
          <IconButton size="small" color="primary" onClick={() => handleVerMas(params.row)}>
            <Visibility />
          </IconButton>
        </Tooltip>
      )
    },
  ];

  return (
    <Container maxWidth={isMobile ? false : "xl"} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={History}
          titulo="Acciones de Usuario"
          ayuda="Registro de navegación de cada usuario"
        />
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          {isMobile ? (
            <>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="dd/MM/yyyy"
                customInput={
                  <TextField size="small" fullWidth InputProps={{
                    startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
                  }} />
                }
              />
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="dd/MM/yyyy"
                customInput={
                  <TextField size="small" fullWidth InputProps={{
                    startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
                  }} />
                }
              />
            </>
          ) : (
            <>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd/MM/yyyy"
                customInput={<TextField label="Fecha Desde" size="small" />}
              />
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="dd/MM/yyyy"
                customInput={<TextField label="Fecha Hasta" size="small" />}
              />
            </>
          )}
          <Button onClick={handleUpdateData} variant="contained">
            Buscar
          </Button>
        </Box>
        <TextField
          label="Buscar por Navegó"
          value={searchTerm}
          onChange={handleSearchChange}
          fullWidth
          sx={{ mb: 2 }}
        />
        {loading ? (
          <Cargando></Cargando>
        ) : isMobile ? (
          <Stack spacing={1}>
            {filteredUsers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Sin resultados.</Typography>
            )}
            {filteredUsers.map((row) => (
              <Paper key={row.id} variant="outlined" onClick={() => handleVerMas(row)} sx={{ p: 1.2, cursor: 'pointer' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.username || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>{row.navego}</Typography>
                  </Box>
                  <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleVerMas(row); }}><Visibility fontSize="small" /></IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                  <Chip size="small" icon={<CalendarToday sx={{ fontSize: 13 }} />} label={row.fecha} variant="outlined" />
                  <Chip size="small" icon={<Place sx={{ fontSize: 13 }} />} label={row.ip || '—'} variant="outlined" />
                </Box>
              </Paper>
            ))}
          </Stack>
        ) : (
          <div style={{ height: 600, width: '100%' }}>
            <DataGrid rows={filteredUsers} columns={columns} pageSizeOptions={[25, 50, 100]} components={{ Toolbar: ExportToolbar }} />
          </div>
        )}
      </Box>

      <AccionDetailDialog
        open={dialogOpen}
        row={selectedRow}
        onClose={() => setDialogOpen(false)}
      />
    </Container>
  );
}

export default UsuarioAccionesList;
